import type {
  CandidateArticle,
  ReaderHint,
} from "@/features/material-compliance/api/types"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { StatementCard } from "../useStudioV2State"

/** One line to render: the copy key and the sheet row it points at. Never
 *  the cell text and never the backend's own sentence. */
export interface ReaderHintLine {
  kind: string
  key: TranslationKeys
  sheet: string
  row: number
}

/** The shown kinds, worst first: the order decides which lines survive a
 *  card's cap (the backend's `_READER_HINT_RANK` uses the same order). */
const NOTES = new Map<string, { key: TranslationKeys; rank: number }>([
  [
    "inquiry-template-disclosure-without-tick",
    { key: "MC_DECL_V2_READER_HINT_DISCLOSURE_WITHOUT_TICK", rank: 0 },
  ],
  [
    "inquiry-template-merged-tick-rows",
    { key: "MC_DECL_V2_READER_HINT_MERGED_TICK_ROWS", rank: 1 },
  ],
  [
    "inquiry-template-conditions-without-disclosure",
    { key: "MC_DECL_V2_READER_HINT_CONDITIONS_WITHOUT_DISCLOSURE", rank: 2 },
  ],
  [
    "inquiry-template-list-edition-differs",
    { key: "MC_DECL_V2_READER_HINT_LIST_EDITION_DIFFERS", rank: 3 },
  ],
])

/**
 * ONE reader hint kind mapped to this app's own copy. Null for a kind this
 * build does not know, which renders nothing (same rule as `scopeFlagNote`):
 * a newer backend's kind stays silent rather than showing a developer
 * sentence.
 */
export function readerHintNote(kind: string): TranslationKeys | null {
  return NOTES.get(kind)?.key ?? null
}

/**
 * The articles whose hints a statement card shows. One rule for both masks:
 * a card the reader or the model built shows every hint of the articles it
 * covers (a blanket statement the whole pool, a subset its own numbers plus
 * what M2 resolved it to), so an extracted "all compliant" card cannot hide
 * a no-tick substance. A manually added card shows none (a reviewer-created
 * statement resolves the hint), nor does an excluded one. The remainder card
 * shows every hint of the articles it still holds.
 */
export function cardHintArticles(
  card: StatementCard,
  pool: readonly CandidateArticle[],
): string[] {
  if (card.manuallyAdded || card.excluded) return []
  if (card.scopeAll) return pool.map((a) => a.articleNumber)
  return [...card.articleNumbers, ...card.resolvedArticles]
}

/**
 * The lines a card shows for `articles`: every known hint, one per (kind,
 * sheet, row) however many of the articles share the row, ordered by kind
 * (worst first), then sheet, then row. `byArticle` is one Verordnung's hints
 * keyed by article number (`useStudioV2State`'s `readerHintsByFramework`).
 */
export function readerHintLines(
  byArticle: ReadonlyMap<string, readonly ReaderHint[]> | undefined,
  articles: Iterable<string>,
): ReaderHintLine[] {
  if (!byArticle?.size) return []
  const lines = new Map<string, ReaderHintLine & { rank: number }>()
  for (const article of articles) {
    for (const hint of byArticle.get(article) ?? []) {
      const note = NOTES.get(hint.kind)
      if (!note) continue
      const id = `${hint.kind}\u0000${hint.sheet}\u0000${hint.row}`
      if (!lines.has(id))
        lines.set(id, {
          kind: hint.kind,
          key: note.key,
          sheet: hint.sheet,
          row: hint.row,
          rank: note.rank,
        })
    }
  }
  return [...lines.values()]
    .sort(
      (a, b) =>
        a.rank - b.rank || a.sheet.localeCompare(b.sheet) || a.row - b.row,
    )
    .map(({ kind, key, sheet, row }) => ({ kind, key, sheet, row }))
}
